import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Sparkles, 
  Rocket, 
  ArrowLeft, 
  CheckCircle, 
  Loader2,
  Zap,
  Target,
  BarChart3,
  Calendar,
  Clock,
  Shield,
  TrendingUp,
  Users,
  Megaphone,
  LineChart,
  Flag,
  Flame
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// PAGE COPILOTE - PROCHAINEMENT DISPONIBLE
// Cellule d'accompagnement sur 12 mois
// pour piloter des campagnes d'acquisition
// =============================================

const CopilotePage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await api.post('/newsletter/copilote-waitlist', {
        ...formData,
        source: 'copilote_landing',
        subscribed_at: new Date().toISOString()
      });
      setSubmitted(true);
      toast.success('Inscription réussie !');
    } catch (error) {
      // Even if API fails, show success (we'll handle backend later)
      setSubmitted(true);
      toast.success('Inscription réussie !');
    } finally {
      setLoading(false);
    }
  };

  // Programme sur 12 mois - phases clés
  const programPhases = [
    { 
      month: "Mois 1-2", 
      title: "Audit & Stratégie", 
      icon: Target,
      items: ["Analyse de votre marché", "Définition des objectifs", "Stratégie d'acquisition"]
    },
    { 
      month: "Mois 3-6", 
      title: "Déploiement", 
      icon: Rocket,
      items: ["Mise en place des campagnes", "Création des contenus", "Optimisation continue"]
    },
    { 
      month: "Mois 7-10", 
      title: "Accélération", 
      icon: TrendingUp,
      items: ["Scaling des campagnes", "Tests A/B avancés", "Diversification canaux"]
    },
    { 
      month: "Mois 11-12", 
      title: "Autonomie", 
      icon: Flag,
      items: ["Transfert de compétences", "Documentation", "Plan de continuité"]
    }
  ];

  // Inclus dans l'accompagnement
  const included = [
    { icon: BarChart3, text: "Pilotage de vos campagnes Ads" },
    { icon: Megaphone, text: "Stratégie d'acquisition personnalisée" },
    { icon: LineChart, text: "Reporting mensuel détaillé" },
    { icon: Users, text: "Réunions de suivi hebdomadaires" },
    { icon: Calendar, text: "Planning éditorial sur mesure" },
    { icon: Shield, text: "Gestion de budget optimisée" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-950 text-white relative overflow-hidden">
      {/* Background blurred content - simulating the existing page */}
      <div className="absolute inset-0 z-0">
        {/* Simulated blurred page content */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-950">
          {/* Decorative elements to simulate content */}
          <div className="absolute top-20 left-10 w-64 h-64 rounded-3xl bg-gradient-to-br from-violet-600/20 to-indigo-600/20 blur-xl" />
          <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-gradient-to-br from-cyan-500/10 to-blue-500/10 blur-2xl" />
          <div className="absolute bottom-40 left-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-orange-500/10 to-red-500/10 blur-3xl" />
          
          {/* Simulated content blocks - blurred */}
          <div className="max-w-7xl mx-auto px-4 pt-24 filter blur-sm opacity-30 pointer-events-none">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="h-8 w-48 bg-white/20 rounded-full mb-6" />
                <div className="h-16 w-full bg-white/10 rounded-lg mb-4" />
                <div className="h-16 w-3/4 bg-white/10 rounded-lg mb-8" />
                <div className="space-y-3">
                  <div className="h-4 w-full bg-white/5 rounded" />
                  <div className="h-4 w-5/6 bg-white/5 rounded" />
                  <div className="h-4 w-4/6 bg-white/5 rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-32 bg-white/5 rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Blur overlay */}
        <div className="absolute inset-0 backdrop-blur-md bg-slate-900/60" />
      </div>

      {/* Floating decorative elements */}
      <div className="fixed top-1/4 left-10 w-32 h-32 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none z-0" />
      <div className="fixed bottom-1/4 right-10 w-40 h-40 rounded-full bg-violet-500/10 blur-3xl pointer-events-none z-0" />
      <div className="fixed top-1/2 right-1/4 w-24 h-24 rounded-full bg-orange-500/10 blur-2xl pointer-events-none z-0" />

      {/* Main content - on top */}
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/site" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour au site
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">COPILOTE</span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-12">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 mb-6">
                <Rocket className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-300">Prochainement disponible</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 leading-tight">
                Cellule d'accompagnement
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">
                  sur 12 mois
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8">
                Un programme complet pour mettre en place et co-piloter 
                votre stratégie marketing d'acquisition.
              </p>
            </div>

            {/* Form Card - Central element */}
            <div className="max-w-xl mx-auto mb-16">
              <Card className="bg-white/10 backdrop-blur-xl border-white/20 rounded-2xl overflow-hidden shadow-2xl">
                <CardContent className="p-6 md:p-8">
                  {submitted ? (
                    <div className="text-center py-6">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold mb-4">Merci {formData.first_name} !</h2>
                      <p className="text-white/70 mb-6">
                        Vous êtes inscrit(e) sur la liste d'attente COPILOTE.
                        <br />
                        Nous vous contacterons dès l'ouverture du programme !
                      </p>
                      <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10">
                        <Link to="/site">Retour au site</Link>
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6 text-center">
                        <h2 className="text-xl md:text-2xl font-bold mb-2">Rejoindre la liste d'attente</h2>
                        <p className="text-white/60 text-sm">
                          Places limitées • Programme sous réserve d'éligibilité
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-white/80">Prénom *</Label>
                            <Input 
                              value={formData.first_name}
                              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                              placeholder="Jean"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white/80">Nom *</Label>
                            <Input 
                              value={formData.last_name}
                              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                              placeholder="Dupont"
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white/80">Entreprise</Label>
                          <Input 
                            value={formData.company}
                            onChange={(e) => setFormData({...formData, company: e.target.value})}
                            placeholder="Ma Société"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white/80">Téléphone</Label>
                          <Input 
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            placeholder="06 12 34 56 78"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-white/80">Email *</Label>
                          <Input 
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="jean@exemple.fr"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                            required
                          />
                        </div>

                        <Button 
                          type="submit" 
                          disabled={loading}
                          className="w-full h-12 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold rounded-xl"
                        >
                          {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <Rocket className="w-5 h-5 mr-2" />
                              Rejoindre la liste d'attente
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-white/40 text-center">
                          En vous inscrivant, vous acceptez d'être recontacté(e) pour le programme COPILOTE.
                        </p>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Programme sur 12 mois */}
            <div className="mb-16">
              <h3 className="text-xl font-bold text-center mb-8">
                <Clock className="w-5 h-5 inline mr-2 text-orange-400" />
                Programme sur 12 mois
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {programPhases.map((phase, idx) => {
                  const Icon = phase.icon;
                  return (
                    <div 
                      key={idx} 
                      className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-all"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center">
                          <Icon className="w-4 h-4 text-orange-400" />
                        </div>
                        <span className="text-xs text-orange-300 font-medium">{phase.month}</span>
                      </div>
                      <h4 className="font-semibold text-white mb-2">{phase.title}</h4>
                      <ul className="space-y-1">
                        {phase.items.map((item, i) => (
                          <li key={i} className="text-xs text-white/50 flex items-start gap-1.5">
                            <CheckCircle className="w-3 h-3 text-orange-400 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ce qui est inclus */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-center mb-8">
                <Zap className="w-5 h-5 inline mr-2 text-cyan-400" />
                Inclus dans l'accompagnement
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {included.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={idx} 
                      className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3"
                    >
                      <Icon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span className="text-sm text-white/70">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-center text-white/60 pb-8">
              <div>
                <div className="text-2xl font-bold text-white">12</div>
                <div className="text-sm">mois d'accompagnement</div>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div>
                <div className="text-2xl font-bold text-white">1</div>
                <div className="text-sm">consultant dédié</div>
              </div>
              <div className="w-px h-10 bg-white/20 hidden sm:block" />
              <div>
                <div className="text-2xl font-bold text-white">Q1 2025</div>
                <div className="text-sm">Lancement prévu</div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CopilotePage;
